/**
 * Send a PostHog person-property batch only when it CHANGED for this user —
 * across app launches and page loads, not just within one session.
 *
 * Every `setPersonProperties` (and every repeat `identify` with properties for
 * an already-identified user) is a billed `$set` event. The SDK de-duplicates
 * against ONE in-memory slot, so two callers with different batches (account
 * props + social props) defeat it by alternating, and a cold start always
 * re-sends. Measured 2026-10-13: ~304k `$set`/day for ~55k people, almost all
 * of them values that had not changed.
 *
 * The store is injected (MMKV on native, localStorage on web); it only needs
 * synchronous string get/set and may throw — a failing store just means we
 * send, which is the old behaviour.
 */
export interface PersonPropsStore {
  get(key: string): string | null | undefined;
  set(key: string, value: string): void;
}

/** Key-order-independent serialization, so `{a,b}` and `{b,a}` are one value. */
export function stablePersonProps(props: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(props)
      .sort()
      .map((k) => [k, props[k]]),
  );
}

const storeKey = (slot: string, userId: string) => `ph-person-props:${slot}:${userId}`;

/**
 * True when `props` differ from what was last sent for `userId` under `slot`
 * (and records them as sent) — the caller then sends. False means "already
 * sent, skip". Call it only right before actually sending.
 */
export function personPropsChanged(
  store: PersonPropsStore,
  slot: string,
  userId: string,
  props: Record<string, unknown>,
): boolean {
  const next = stablePersonProps(props);
  const key = storeKey(slot, userId);
  try {
    if (store.get(key) === next) return false;
  } catch {
    return true;
  }
  try {
    store.set(key, next);
  } catch {
    /* send anyway */
  }
  return true;
}
